import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { SUPABASE_URL } from '@/lib/env';
import { Browser } from '@capacitor/browser';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Analytics } from '@/lib/analytics';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signInWithGoogle: () => Promise<{ error: any }>;
  signInWithApple: () => Promise<{ error: any }>;
  signOut: () => Promise<{ error: any }>;
  verifyOtp: (email: string, token: string) => Promise<{ error: any }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = React.useState<User | null>(null);
  const [session, setSession] = React.useState<Session | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        if (session?.user) {
          Analytics.setUserId(session.user.id, session.user.email ?? null);
        } else if (event === 'SIGNED_OUT') {
          Analytics.setUserId(null);
        }
        
        // Redirect to reset password page on password recovery
        if (event === 'PASSWORD_RECOVERY') {
          window.location.href = '/reset-password';
          return;
        }
        
        // Close in-app browser on successful auth in native apps (ignore if no browser was open)
        if (event === 'SIGNED_IN' && Capacitor.isNativePlatform()) {
          Browser.close().catch(() => {});
        }
      }
    );

    // THEN check for existing session (always clear loading — never strand the UI)
    const sessionTimeout = window.setTimeout(() => {
      console.warn("[Auth] getSession timed out — showing auth screen");
      setLoading(false);
    }, 4000);
    void supabase.auth
      .getSession()
      .then(({ data: { session }, error }) => {
        if (error) {
          console.error("[Auth] getSession failed:", error.message);
        }
        setSession(session);
        setUser(session?.user ?? null);
      })
      .catch((e) => {
        console.error("[Auth] getSession threw:", e);
      })
      .finally(() => {
        window.clearTimeout(sessionTimeout);
        setLoading(false);
      });

    // Handle OAuth redirect on web (process tokens in URL)
    // IMPORTANT: Skip on /auth/callback — the dedicated AuthCallback page handles
    // the code exchange there. Running both races to consume the same one-time
    // OAuth code, causing "invalid grant" errors and a reload loop.
    if (!Capacitor.isNativePlatform() && window.location.pathname !== '/auth/callback') {
      try {
        const hash = window.location.hash;
        const search = window.location.search;

        // Implicit flow: tokens in hash fragment
        if (hash && hash.includes('access_token')) {
          console.log('OAuth tokens found in hash, setting session...');
          const hashParams = new URLSearchParams(hash.substring(1));
          const access_token = hashParams.get('access_token') || undefined;
          const refresh_token = hashParams.get('refresh_token') || undefined;

          if (access_token && refresh_token) {
            supabase.auth.setSession({ access_token, refresh_token })
              .then(({ data, error }) => {
                if (error) {
                  console.error('Error setting session from hash:', error);
                } else {
                  console.log('Session set successfully from hash');
                }
                // Clean URL and continue directly into protected routing.
                window.history.replaceState({}, document.title, '/dashboard');
                window.location.replace('/dashboard');
              })
              .catch((e) => console.error('Error setting session from hash:', e));
          }
        }

        // PKCE flow: code in query string
        const url = new URL(window.location.href);
        const code = url.searchParams.get('code');
        if (code) {
          console.log('OAuth code found, exchanging for session...');
          supabase.auth.exchangeCodeForSession(code)
            .then(({ data, error }) => {
              if (error) {
                console.error('Error exchanging code for session (web):', error);
              } else {
                console.log('Session established successfully via code exchange');
              }
              // Clean URL and continue directly into protected routing.
              window.history.replaceState({}, document.title, '/dashboard');
              window.location.replace('/dashboard');
            })
            .catch((e) => console.error('Error exchanging code for session (web):', e));
        }
      } catch (e) {
        console.error('Error handling OAuth redirect (web):', e);
      }
    }

    // Handle deep links for OAuth callback in native apps
    let appUrlListener: any;
    if (Capacitor.isNativePlatform()) {
      App.addListener('appUrlOpen', async (data) => {
        // Handle the OAuth callback URL
        if (data.url) {
          console.log('Deep link received:', data.url);
          
          try {
            const url = new URL(data.url);
            
            // Handle custom scheme URLs (striveai://auth/callback)
            if (url.protocol === 'striveai:') {
              console.log('Processing striveai:// deep link');
              
              // Extract OAuth params
              const hashParams = new URLSearchParams(url.hash.substring(1));
              let access_token = hashParams.get('access_token');
              let refresh_token = hashParams.get('refresh_token');
              const code = url.searchParams.get('code');
              const error_description = url.searchParams.get('error_description');

              if (error_description) {
                console.error('OAuth error:', error_description);
              }

              try {
                if (code) {
                  // PKCE flow (recommended by Supabase) – exchange code for a session
                  console.log('Code found, exchanging for session');
                  const { error } = await supabase.auth.exchangeCodeForSession(code);
                  if (error) {
                    console.error('Error exchanging code for session:', error);
                  } else {
                    console.log('Session established via code exchange');
                  }
                } else if (access_token && refresh_token) {
                  // Legacy implicit flow fallback
                  console.log('Tokens found, setting session');
                  const { error } = await supabase.auth.setSession({
                    access_token,
                    refresh_token,
                  });
                  if (error) {
                    console.error('Error setting session:', error);
                  } else {
                    console.log('Session set successfully');
                  }
                } else {
                  console.log('No code or tokens found in URL');
                }
              } finally {
                // Always close the browser
                await Browser.close().catch(e => console.log('Browser already closed'));
              }
            }
          } catch (error) {
            console.error('Error processing deep link:', error);
            await Browser.close().catch(e => console.log('Browser already closed'));
          }
        }
      }).then((listener) => {
        appUrlListener = listener;
      });
    }

    return () => {
      window.clearTimeout(sessionTimeout);
      subscription.unsubscribe();
      if (appUrlListener) {
        appUrlListener.remove();
      }
    };
  }, []);

  const signUp = async (email: string, password: string) => {
    // Use production URL for native platforms since capacitor://localhost won't work for email verification
    const redirectUrl = Capacitor.isNativePlatform() 
      ? 'https://straiv.app/' 
      : `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl
      }
    });
    
    if (!error) {
      Analytics.signUp('email');
    }
    
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (!error) {
      Analytics.login('email');
    }
    
    return { error };
  };

  const signInWithGoogle = async () => {
    try {
      // Native mobile: use Capacitor Google Auth plugin
      if (Capacitor.isNativePlatform()) {
        try {
          const { GoogleAuth } = await import('@codetrix-studio/capacitor-google-auth');
          // CRITICAL: Initialize immediately before signIn to fix NullPointerException on Android (3.4.0-rc.4)
          const platform = Capacitor.getPlatform();
          const clientId = platform === 'ios'
            ? '5370505998-758l7b9hr488v29cefv5t71uied9gvv7.apps.googleusercontent.com'
            : platform === 'android'
              ? '656278119230-qep1aon07kqn1f7prjpar3urq6c8bbdr.apps.googleusercontent.com'
              : '5370505998-hakrhril4cl729qhlhg5gq2c67c9hds9.apps.googleusercontent.com';
          await GoogleAuth.initialize({
            clientId,
            scopes: ['profile', 'email'],
            grantOfflineAccess: true,
          });
          const googleUser = await GoogleAuth.signIn();
          
          if (!googleUser.authentication?.idToken) {
            return { error: new Error('No ID token received from Google') };
          }

          // Log EVERYTHING Google returns so we can debug
          console.log('=== GOOGLE SIGN-IN RESPONSE ===');
          console.log('Email:', googleUser.email);
          console.log('Given Name:', googleUser.givenName);
          console.log('Family Name:', googleUser.familyName);
          console.log('Full Name:', googleUser.name);
          console.log('Full response:', JSON.stringify(googleUser));
          console.log('===============================');

          // Extract name from Google response BEFORE any async operations
          let firstName = googleUser.givenName || null;
          let lastName = googleUser.familyName || null;
          
          // Fallback: split full name if available
          if (!firstName && googleUser.name && googleUser.name.includes(' ')) {
            const nameParts = googleUser.name.trim().split(' ');
            firstName = nameParts[0] || null;
            lastName = nameParts.slice(1).join(' ') || null;
          }
          
          // IMMEDIATELY store in sessionStorage so Onboarding can access it
          // This bypasses the database race condition
          if (firstName) {
            sessionStorage.setItem('social_signin_first_name', firstName);
            console.log('Stored firstName in sessionStorage:', firstName);
          }
          if (lastName) {
            sessionStorage.setItem('social_signin_last_name', lastName);
            console.log('Stored lastName in sessionStorage:', lastName);
          }

          // Sign in to Supabase with the Google ID token
          console.log('=== SUPABASE SIGN-IN ATTEMPT ===');
          console.log('Provider: google');
          console.log('Token (first 50 chars):', googleUser.authentication.idToken.substring(0, 50) + '...');
          
          const { data, error } = await supabase.auth.signInWithIdToken({
            provider: 'google',
            token: googleUser.authentication.idToken,
          });

          console.log('=== SUPABASE SIGN-IN RESULT ===');
          console.log('Data:', data ? JSON.stringify({ user: { id: data.user?.id, email: data.user?.email } }) : 'null');
          if (error) {
            console.log('Error:', JSON.stringify(error));
            console.log('Error message:', error.message);
            console.log('Error status:', error.status);
            console.log('Error name:', error.name);
            // @ts-ignore
            console.log('Error details:', error.details || error.hint || 'none');
          } else {
            console.log('Error: null');
          }
          console.log('================================');

          if (!error && data?.user?.id) {
            Analytics.login('google');
            Analytics.setUserId(data.user.id);
            console.log('Google provided name:', firstName, lastName);
            
            // Also save to profile as backup (async, non-blocking)
            if (firstName) {
              // Wait a moment for Supabase trigger to create profile first
              setTimeout(async () => {
                const { error: updateError } = await supabase
                  .from('profiles')
                  .update({
                    first_name: firstName,
                    last_name: lastName || null,
                    updated_at: new Date().toISOString(),
                  })
                  .eq('id', data.user!.id);
                
                if (updateError) {
                  console.error('Error updating profile with Google name:', updateError);
                } else {
                  console.log('Profile updated with Google name:', firstName, lastName);
                }
              }, 500);
            }
          }

          return { error };
        } catch (nativeError: any) {
          console.error('=== NATIVE GOOGLE SIGN-IN ERROR ===');
          console.error('Error:', nativeError);
          console.error('Error message:', nativeError?.message);
          console.error('Error code:', nativeError?.code);
          console.error('Error stack:', nativeError?.stack);
          console.error('Full error object:', JSON.stringify(nativeError, Object.getOwnPropertyNames(nativeError), 2));
          console.error('Platform:', Capacitor.getPlatform());
          console.error('===================================');
          return { error: nativeError || new Error(`Google Sign-In failed: ${nativeError?.message || 'Unknown error'}`) };
        }
      } 
      
      // Web: use standard OAuth flow
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      });

      return { error };
    } catch (error: any) {
      console.error('Google sign-in error:', error);
      return { error };
    }
  };

  const signInWithApple = async () => {
    try {
      // Native iOS: use Capacitor Apple Sign In plugin
      if (Capacitor.getPlatform() === 'ios') {
        try {
          const { SignInWithApple } = await import('@capacitor-community/apple-sign-in');
          
          const result = await SignInWithApple.authorize({
            clientId: 'com.striveai.fitness', // Your app's bundle ID
            redirectURI: `${SUPABASE_URL}/auth/v1/callback`,
            scopes: 'email name',
          });
          
          if (!result.response?.identityToken) {
            return { error: new Error('No identity token received from Apple') };
          }

          // Log EVERYTHING Apple returns so we can debug
          console.log('=== APPLE SIGN-IN RESPONSE ===');
          console.log('Email:', result.response.email);
          console.log('Given Name:', result.response.givenName);
          console.log('Family Name:', result.response.familyName);
          console.log('User ID:', result.response.user);
          console.log('Full response:', JSON.stringify(result.response));
          console.log('==============================');
          
          // Extract name from Apple response BEFORE any async operations
          // Apple only provides givenName/familyName on FIRST sign-in
          const firstName = result.response.givenName || null;
          const lastName = result.response.familyName || null;
          
          // IMMEDIATELY store in sessionStorage so Onboarding can access it
          // This bypasses the database race condition
          if (firstName) {
            sessionStorage.setItem('social_signin_first_name', firstName);
            console.log('Stored firstName in sessionStorage:', firstName);
          }
          if (lastName) {
            sessionStorage.setItem('social_signin_last_name', lastName);
            console.log('Stored lastName in sessionStorage:', lastName);
          }
          
          console.log('Apple token received, sending to Supabase...');
          
          // Sign in to Supabase with the Apple ID token
          const { data, error } = await supabase.auth.signInWithIdToken({
            provider: 'apple',
            token: result.response.identityToken,
          });

          if (error) {
            console.error('Supabase Apple auth error:', error);
          } else {
            console.log('Supabase Apple auth success:', data?.user?.id);
            Analytics.login('apple');
            if (data?.user?.id) {
              Analytics.setUserId(data.user.id);
            }
            console.log('Apple provided name:', firstName, lastName);
            
            // Also save to profile as backup (async, non-blocking)
            if (data?.user?.id && firstName) {
              // Wait a moment for Supabase trigger to create profile first
              setTimeout(async () => {
                const { error: updateError } = await supabase
                  .from('profiles')
                  .update({
                    first_name: firstName,
                    last_name: lastName || null,
                    updated_at: new Date().toISOString(),
                  })
                  .eq('id', data.user!.id);
                
                if (updateError) {
                  console.error('Error updating profile with Apple name:', updateError);
                } else {
                  console.log('Profile updated with Apple name:', firstName, lastName);
                }
              }, 500);
            } else if (!firstName) {
              console.log('Apple did not provide name (not first sign-in), skipping profile update');
            }
          }

          return { error };
        } catch (nativeError: any) {
          console.error('Native Apple sign-in error:', nativeError);
          // If user cancelled, don't show error
          if (nativeError?.message?.includes('cancelled') || nativeError?.code === 1001) {
            return { error: null };
          }
          return { error: new Error('Apple Sign-In failed. Please try again.') };
        }
      }
      
      // Web: use standard OAuth flow
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      return { error };
    } catch (error: any) {
      console.error('Apple sign-in error:', error);
      return { error };
    }
  };

  const signOut = async () => {
    Analytics.logout();
    Analytics.setUserId(null);
    
    const { error } = await supabase.auth.signOut();
    // Gracefully handle missing/expired sessions
    if (error && typeof error.message === 'string' && error.message.toLowerCase().includes('session')) {
      // Treat as success and clear local state
      setUser(null);
      setSession(null);
      return { error: null };
    }
    if (!error) {
      setUser(null);
      setSession(null);
    }
    return { error };
  };

  const verifyOtp = async (email: string, token: string) => {
    const { error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'email'
    });
    return { error };
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    signInWithApple,
    signOut,
    verifyOtp,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};